import Ajv2020, { type ErrorObject, type ValidateFunction } from "ajv/dist/2020";

// The canonical contract, imported from the repository's schemas/ directory.
// The TypeScript validator and the Go validator read the SAME file; neither
// keeps a copy, so the two implementations cannot drift apart silently
// (docs/plans/06-flow-schema-runtime.md).
import flowSchema from "@contracts/flow.schema.json";

/**
 * Which validation layer produced a diagnostic.
 *
 * This mirrors `Layer` in services/api/internal/flow/diagnostics.go. The two
 * layers stay distinguishable in both languages: a malformed document is not
 * the same problem as a well-formed but unexecutable graph.
 */
export type Layer = "schema" | "semantic";

/** Stable diagnostic codes shared with the Go validator. */
export const CODE_SCHEMA_VIOLATION = "schema_violation";

/** One structured validation finding. */
export interface Diagnostic {
  layer: Layer;
  code: string;
  path?: string;
  detail: string;
}

let compiled: ValidateFunction | undefined;

function validator(): ValidateFunction {
  if (!compiled) {
    // strict:false because the schema uses `oneOf` over node variants, which
    // Ajv's strict mode flags as ambiguous even though it is exactly what the
    // contract means. allowUnionTypes covers the nullable branch case value.
    const ajv = new Ajv2020({
      allErrors: true,
      strict: false,
      allowUnionTypes: true,
    });
    // No network access is configured on purpose: a validator that could fetch
    // a remote $ref would make validation depend on the internet and on
    // whatever that URL serves today.
    compiled = ajv.compile(flowSchema);
  }
  return compiled;
}

function describe(error: ErrorObject): Diagnostic {
  const keyword = error.keyword ? `${error.keyword}: ` : "";
  return {
    layer: "schema",
    code: CODE_SCHEMA_VIOLATION,
    path: error.instancePath === "" ? "/" : error.instancePath,
    detail: `${keyword}${error.message ?? "value is not valid"}`,
  };
}

/**
 * Validate a flow document's SHAPE against the Draft 2020-12 contract.
 *
 * Passing proves the JSON is well formed. It proves nothing about ports,
 * references, loops, variables, tenancy or AMD placement: those are the
 * semantic layer, which runs in Go today and is mirrored here for inline
 * designer diagnostics in P2. Never present a clean result from this function
 * as "this flow is valid".
 */
export function validateFlowSchema(document: unknown): Diagnostic[] {
  const validate = validator();
  if (validate(document)) {
    return [];
  }
  return (validate.errors ?? []).map(describe);
}

/** Sort diagnostics deterministically, matching Diagnostics.Sorted in Go. */
export function sortDiagnostics(diagnostics: readonly Diagnostic[]): Diagnostic[] {
  return [...diagnostics].sort(
    (a, b) =>
      a.layer.localeCompare(b.layer) ||
      a.code.localeCompare(b.code) ||
      (a.path ?? "").localeCompare(b.path ?? "") ||
      a.detail.localeCompare(b.detail),
  );
}

/** The distinct diagnostic codes, sorted. Mirrors Diagnostics.Codes in Go. */
export function diagnosticCodes(diagnostics: readonly Diagnostic[]): string[] {
  return [...new Set(diagnostics.map((d) => d.code))].sort();
}
